import { ExternalLink } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ADMIN_BASE_URL, adminHref } from '../../../../config/config'
import {
  SmsDirection,
  type SmsMessage,
  SmsStatus,
  useApplicationSmsMessages,
} from '../../../../models/internalApplication'

function formatDayHeader(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatBubbleTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function dayKey(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function groupByDay(messages: SmsMessage[]): { key: string; label: string; items: SmsMessage[] }[] {
  const groups: { key: string; label: string; items: SmsMessage[] }[] = []
  for (const m of messages) {
    const key = dayKey(m.dateCreated)
    const last = groups.at(-1)
    if (last !== undefined && last.key === key) {
      last.items.push(m)
    } else {
      groups.push({ key, label: formatDayHeader(m.dateCreated), items: [m] })
    }
  }
  return groups
}

function Bubble({ message }: { message: SmsMessage }) {
  const isOutbound = message.direction === SmsDirection.OUTBOUND
  const failed = isOutbound && message.status === SmsStatus.FAILED

  return (
    <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line ${
          isOutbound ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
        }`}
      >
        {message.bodyText}
      </div>
      <div className={`text-muted-foreground mt-1 text-xs ${isOutbound ? 'text-right' : 'text-left'}`}>
        {formatBubbleTime(message.dateCreated)}
      </div>
      {message.agentRunId && (
        <a
          href={adminHref(`${ADMIN_BASE_URL}/insure/agentrun/${message.agentRunId}/change/`)}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 text-xs ${
            isOutbound ? 'self-end' : 'self-start'
          }`}
        >
          Agent Run <ExternalLink className="size-3" />
        </a>
      )}
      {failed && <div className="text-destructive text-right text-xs">Failed to deliver</div>}
    </div>
  )
}

export function SmsMessagesTab({ applicationId }: { applicationId: string }) {
  const { messages, isLoading } = useApplicationSmsMessages(applicationId)

  const groups = groupByDay(messages)
  const first = messages.at(0)

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>SMS Conversation</CardTitle>
        {first && (
          <div className="text-muted-foreground text-sm">
            {first.fromPhone} &rarr; {first.toPhone}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} rowClassName="h-14" />
        ) : messages.length === 0 ? (
          <p className="text-muted-foreground text-sm">No SMS messages yet.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-2">
                <div data-testid="sms-day-header" className="text-muted-foreground self-center text-xs">
                  {group.label}
                </div>
                {group.items.map((m) => (
                  <Bubble key={m.id} message={m} />
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
